import { useQuery } from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { api } from 'lib/request/api';

/** Query-key prefix for the area behind the detail route. */
export const CURRENT_AREA_QUERY_KEY = 'area-geometries:current' as const;

export interface CurrentArea<T> {
  loading: boolean;
  /** Why the area couldn't be loaded, or undefined. */
  error?: string;
  data?: T;
  // Re-reads it. A workspace can promote the area, take it off the map, or be the first
  // one opened on it, and all three change what the detail page shows.
  refetch: () => void;
}

// ky's `api` carries a prefixUrl, which rejects a leading slash — and every href the
// backend hands out has one.
const apiPath = (href: string): string => href.replace(/^\//, '');

// A stored consumer reference is an integer version id; the detail route's own param is
// a business id. Both reach this page — the scenario and DCA panels link by the version
// they pinned — so which one it is decides how the area is found.
const isVersionId = (routeId: string): boolean => /^\d+$/.test(routeId);

// Where the area being asked for lives. A business id reads the current promoted
// version from Postgres (current_area_geometries), so the detail follows a promote with
// no search-index lag; a version id is already exact.
const resolveHref = (routeId: string): string => {
  return isVersionId(routeId)
    ? `/area_geometries/${routeId}`
    : `/current_area_geometries/${routeId}`;
};

// The backend's own wording where it gave one, so a refused load says why rather than
// reporting every cause as the same sentence.
const failureMessage = async (error: unknown): Promise<string> => {
  if (error instanceof HTTPError) {
    if (error.response.status === 404) {
      return 'Area not found';
    }
    const body = (await error.response.json().catch(() => null)) as {
      errors?: Array<{ message?: string } | string>;
    } | null;
    const first = body?.errors?.[0];
    const message = typeof first === 'string' ? first : first?.message;
    if (message) {
      return message;
    }
  }
  return 'Failed to load area';
};

/**
 * The area the detail route is pointing at. The route is keyed on business_id, so a
 * bookmark always resolves to whichever version is current; a link that pinned a
 * version — the scenario panels store the integer id — still opens that one.
 *
 * @param {string | undefined} routeId - The route param: a business id or a version id.
 * @return {CurrentArea} The area, whether it is still loading, and why it failed.
 */
export const useCurrentArea = <T>(
  routeId: string | undefined
): CurrentArea<T> => {
  const { data, isFetching, error, refetch } = useQuery<T, Error>({
    queryKey: [CURRENT_AREA_QUERY_KEY, routeId],
    queryFn: async ({ signal }) => {
      try {
        const href = resolveHref(routeId as string);
        return await api.get(apiPath(href), { signal }).json<T>();
      } catch (requestError) {
        // Carried as the message so the page shows the reason, not a class name.
        throw new Error(await failureMessage(requestError));
      }
    },
    enabled: !!routeId,
    // The detail page reports a failure rather than retrying behind a spinner.
    retry: false,
  });

  return {
    // Only the initial load (fetching with nothing to show yet) blanks the page to
    // a spinner. A background refetch — staleTime: 0 remount, reconnect — keeps the
    // area on screen rather than flashing the spinner over content already shown;
    // and a disabled query (no routeId) isn't loading at all.
    loading: isFetching && data === undefined,
    error: error ? error.message : undefined,
    data,
    refetch: () => {
      void refetch();
    },
  };
};
